/**
 * Light or dark follows the operating system (the console shell spec, rule 05), unless the person
 * picked one for this browser (P8-54): "light" or "dark" in localStorage overrides
 * `prefers-color-scheme`; "system", or nothing, follows it. Arc switches on `data-theme` on
 * <html>, so this keeps that attribute in step, and a page's `theme-color` (the phone's status
 * bar in an installed app) with the theme's background, majitap.css's `--background`.
 *
 * THEME_SCRIPT runs inline in <head> before the first paint, so a dark-mode user never sees a
 * white flash. Vite apps inject it with `themeScriptPlugin` (see apps/console/vite.config.ts).
 * The function is serialised into that script, so it uses nothing from outside its body.
 */
export function followSystemTheme(root: HTMLElement = document.documentElement): () => void {
  const query = window.matchMedia("(prefers-color-scheme: dark)");
  const apply = () => {
    let stored: string | null = null;
    try {
      stored = window.localStorage.getItem("majitap-theme");
    } catch {
      // storage blocked (private window, cleared site data): follow the system
    }
    const theme = stored === "light" || stored === "dark" ? stored : query.matches ? "dark" : "light";
    root.setAttribute("data-theme", theme);
    // the dark background is the brand's (ADR 0023); light is white in both
    const darkBackground = root.getAttribute("data-brand") === "maji" ? "#031a38" : "#090b0c";
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute("content", theme === "dark" ? darkBackground : "#ffffff");
  };
  apply();
  query.addEventListener("change", apply);
  // another tab chose ("storage"), or this one did (setThemePreference)
  window.addEventListener("storage", apply);
  window.addEventListener("majitap-theme", apply);
  return () => {
    query.removeEventListener("change", apply);
    window.removeEventListener("storage", apply);
    window.removeEventListener("majitap-theme", apply);
  };
}

export const THEME_SCRIPT = `(${followSystemTheme.toString()})();`;

/** THEME_SCRIPT as a Vite `transformIndexHtml` tag, at the end of <head>, before the first paint. */
export const themeHeadTag = { tag: "script", children: THEME_SCRIPT, injectTo: "head" } as const;

export type Brand = "maji";

/**
 * themeHeadTag for an app on a brand (ADR 0023): sets `data-brand` on <html> first, so the
 * brand's tokens in majitap.css and its status-bar colour apply from the first paint.
 */
export function brandedThemeHeadTag(brand: Brand) {
  const setBrand = `document.documentElement.setAttribute("data-brand",${JSON.stringify(brand)});`;
  return { tag: "script", children: setBrand + THEME_SCRIPT, injectTo: "head" } as const;
}

export type ThemeChoice = "light" | "dark" | "system";

/** This browser's choice; "system" when there is none or storage is blocked. */
export function getThemePreference(): ThemeChoice {
  try {
    const stored = window.localStorage.getItem("majitap-theme");
    return stored === "light" || stored === "dark" ? stored : "system";
  } catch {
    return "system";
  }
}

/** Remembers the choice for this browser and applies it at once. */
export function setThemePreference(choice: ThemeChoice) {
  try {
    if (choice === "system") window.localStorage.removeItem("majitap-theme");
    else window.localStorage.setItem("majitap-theme", choice);
  } catch {
    // storage blocked: the choice lasts until the page reloads
    document.documentElement.setAttribute(
      "data-theme",
      choice === "system" ? (window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light") : choice,
    );
    return;
  }
  window.dispatchEvent(new Event("majitap-theme"));
}
